import clsx from 'clsx';
import {FC, useMemo} from 'react';

import {NotificationMessage} from '@/hooks/useGetNotifications';
import {Box} from '@mui/material';
import {NotificationCard, NotificationCardVariants} from './NotificationCard';
import {NotificationBadgeProps} from './types';

export type NotificationsListProps = {
  variant: NotificationCardVariants;
  messages: NotificationMessage[] | (() => NotificationMessage[]);
  selected?: NotificationMessage;
  onClickNotification?: (message: NotificationMessage) => void;
  viewProps?: {
    leftBadges?: FC<NotificationBadgeProps>[];
    rightBadges?: FC<NotificationBadgeProps>[];
    actionBadges?: FC<NotificationBadgeProps>[];
    headerBadges?: FC<NotificationBadgeProps>[];
  };
  onChangeMessage?: (message: NotificationMessage) => void;
  className?: string;
};

/**
 * Notifications List extending KBox with view variants 'preview', 'list' and 'expanded'
 * using NotificationView.
 *
 * Optionally renders as selected message using selected.
 * Optionally use onClickNotification for message clicked events.
 *
 * @see NotificationCard
 */
export const NotificationsList: FC<NotificationsListProps> = ({
  variant,
  messages: messagesOrGetter,
  selected,
  onClickNotification,
  viewProps,
  onChangeMessage,
  ...boxProps
}) => {
  const messages = useMemo(
    () => (Array.isArray(messagesOrGetter) ? messagesOrGetter : messagesOrGetter()),
    [messagesOrGetter],
  );

  return (
    <Box {...boxProps} className={`NotificationsList ${variant} ${boxProps.className ?? ''}`}>
      {messages?.length === 0 ? (
        <div style={{padding: 20}} data-testid={`notifications-${variant}-none-message`}>
          No new notifications at this time
        </div>
      ) : (
        messages?.map((message) => (
          <NotificationCard
            key={message.id}
            variant={variant}
            message={message}
            className={clsx({selected: selected?.id === message.id})}
            data-testid={`notification-msg-${message.id}`}
            onClick={() => onClickNotification?.(message)}
            onChangeMessage={onChangeMessage}
            {...viewProps}
          />
        ))
      )}
    </Box>
  );
};
